import { CheckRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AiGradientButton } from "src/components/ui/AiGradientButton";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { useGetTaskProgramDetail } from "src/hooks/queries/task-program-mutations";
import {
  useDeleteTrainingQuiz,
  useGetTrainingQuiz,
  usePublishTrainingQuiz,
} from "src/hooks/queries/training-quiz";
import type { TrainingQuizQuestion } from "src/services/backend";
import { getTrainingRevisionLabel } from "./revision-label";
import { useOpenTrainingQuizAgentPanel } from "./useOpenTrainingQuizAgentPanel";

type TrainingQuizAdminSectionProps = {
  orgId: string;
  programId: string;
  documentId: string;
  documentVersionId: string;
  revision: number;
  isRevisionLive?: boolean;
  historicalQuizRevisions?: Array<{
    documentVersionId: string;
    revision: number;
  }>;
  onOpenAgent?: (existingQuestions?: TrainingQuizQuestion[]) => void;
};

const getTraineeName = ({
  firstName,
  lastName,
  email,
}: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}) => [firstName, lastName].filter(Boolean).join(" ") || email;

export const TrainingQuizAdminSection = ({
  orgId,
  programId,
  documentId,
  documentVersionId,
  revision,
  isRevisionLive = true,
  historicalQuizRevisions = [],
  onOpenAgent,
}: TrainingQuizAdminSectionProps) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedDocumentVersionId, setSelectedDocumentVersionId] =
    useState(documentVersionId);
  const quizRevisions = [
    { documentVersionId, revision },
    ...historicalQuizRevisions.filter(
      (item) => item.documentVersionId !== documentVersionId
    ),
  ];
  const selectedQuizRevision =
    quizRevisions.find(
      (item) => item.documentVersionId === selectedDocumentVersionId
    ) ?? quizRevisions[0];
  const isCurrentRevision =
    selectedQuizRevision.documentVersionId === documentVersionId;
  const quizQuery = useGetTrainingQuiz({
    orgId,
    programId,
    documentVersionId: selectedQuizRevision.documentVersionId,
  });
  const programQuery = useGetTaskProgramDetail(orgId, programId);
  const deleteQuiz = useDeleteTrainingQuiz({ orgId });
  const publishQuiz = usePublishTrainingQuiz({ orgId });
  const openAgent = useOpenTrainingQuizAgentPanel();
  const quiz = quizQuery.data;
  const isPublished = !!quiz?.publishedAt;
  const completedTrainees = useMemo(
    () =>
      programQuery.data?.occurrences.flatMap((occurrence) =>
        occurrence.quizId === quiz?.id
          ? occurrence.tasks.flatMap((task) =>
              task.assignee &&
              (task.manualStatus === "DONE" || !!task.trainingSignedAt)
                ? [
                    {
                      taskStateId: task.id,
                      name: getTraineeName(task.assignee),
                    },
                  ]
                : []
            )
          : []
      ) ?? [],
    [programQuery.data?.occurrences, quiz?.id]
  );
  const currentRevisionLabel = `${getTrainingRevisionLabel(revision)}${
    isRevisionLive ? "" : " (draft)"
  }`;
  const isSelectedRevisionLive = isCurrentRevision ? isRevisionLive : true;
  const publishDisabledReason = !isSelectedRevisionLive
    ? "Publish the document revision before publishing its quiz."
    : publishQuiz.isPending
      ? "Publishing quiz…"
      : "";

  useEffect(() => {
    setSelectedDocumentVersionId(documentVersionId);
  }, [documentVersionId]);

  const handleOpenAgent = () => {
    if (onOpenAgent) {
      onOpenAgent(quiz?.questions);
      return;
    }
    openAgent({
      programId,
      documentId,
      documentVersionId: selectedQuizRevision.documentVersionId,
      revision: selectedQuizRevision.revision,
      ...(!isSelectedRevisionLive && { isRevisionLive: false }),
      existingQuestions: quiz?.questions,
    });
  };

  const handleDeleteQuiz = async () => {
    if (!quiz) return;
    await deleteQuiz.mutateAsync({
      programId,
      documentVersionId: selectedQuizRevision.documentVersionId,
      expectedUpdatedAt: quiz.updatedAt,
      expectedState: quiz.publishedAt ? "PUBLISHED" : "DRAFT",
      confirmedCompletedTaskIds: completedTrainees.map(
        ({ taskStateId }) => taskStateId
      ),
    });
    setConfirmDeleteOpen(false);
  };

  const handlePublish = async () => {
    await publishQuiz.mutateAsync({
      programId,
      documentVersionId: selectedQuizRevision.documentVersionId,
    });
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.paper",
        p: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Training quiz
            </Typography>
            <Chip
              size="xxs"
              color={isPublished ? "success" : quiz ? "warning" : "default"}
              label={isPublished ? "Published" : quiz ? "Draft" : "Not drafted"}
            />
          </Stack>
          {quizRevisions.length === 1 && (
            <Typography variant="body2" color="text.secondary">
              {currentRevisionLabel}
            </Typography>
          )}
        </Box>

        <Stack
          component="section"
          aria-label="Training quiz controls"
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ ml: { sm: "auto" } }}
        >
          <AiGradientButton onClick={handleOpenAgent}>
            {quiz ? "Edit with Agent" : "Generate quiz with Agent"}
          </AiGradientButton>
          {quiz && !isPublished && (
            <Tooltip title={publishDisabledReason}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handlePublish}
                  disabled={!!publishDisabledReason}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          )}
          {quiz && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={
                deleteQuiz.isPending || (isPublished && programQuery.isLoading)
              }
            >
              {isPublished ? "Delete published quiz" : "Delete draft"}
            </Button>
          )}
          {quizRevisions.length > 1 && (
            <CompactSelect
              value={selectedQuizRevision.documentVersionId}
              onChange={setSelectedDocumentVersionId}
              ariaLabel="Quiz revision"
              renderValue={() =>
                `${getTrainingRevisionLabel(selectedQuizRevision.revision)}${
                  isCurrentRevision
                    ? isRevisionLive
                      ? " (current)"
                      : " (draft)"
                    : ""
                }`
              }
            >
              {quizRevisions.map((item) => (
                <MenuItem
                  key={item.documentVersionId}
                  value={item.documentVersionId}
                >
                  {getTrainingRevisionLabel(item.revision)}
                  {item.documentVersionId === documentVersionId
                    ? isRevisionLive
                      ? " (current)"
                      : " (draft)"
                    : ""}
                </MenuItem>
              ))}
            </CompactSelect>
          )}
        </Stack>
      </Stack>

      {quizQuery.isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading quiz…
        </Typography>
      )}

      {!quizQuery.isLoading && !quiz && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {isSelectedRevisionLive
            ? "No task will be sent for this revision until its quiz is drafted and published."
            : `Generate and edit this quiz now. It can be published after ${getTrainingRevisionLabel(selectedQuizRevision.revision)} is live.`}
        </Alert>
      )}

      {quiz && (
        <Stack spacing={1.5} sx={{ mt: 2 }} divider={<Divider flexItem />}>
          {quiz.questions.map((question, index) => (
            <Box key={question.id}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {index + 1}. {question.text}
              </Typography>
              <Stack
                component="ul"
                spacing={0.5}
                sx={{ color: "text.secondary", mb: 0, mt: 1, pl: 3 }}
              >
                {question.options.map((option) => {
                  const isCorrect = option.id === question.correctOptionId;

                  return (
                    <Box component="li" key={option.id}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {option.text}
                        </Typography>
                        {isCorrect && (
                          <CheckRounded
                            aria-label="Correct answer"
                            color="success"
                            sx={{ fontSize: 18 }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isPublished ? "Delete published quiz?" : "Delete quiz draft?"}
        </DialogTitle>
        <DialogContent>
          {isPublished ? (
            completedTrainees.length > 0 ? (
              <Stack spacing={2}>
                <Typography variant="body2">
                  This will revert training completed by:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {completedTrainees.map(({ taskStateId, name }) => (
                    <Typography
                      component="li"
                      variant="body2"
                      key={taskStateId}
                    >
                      {name}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2">Continue?</Typography>
              </Stack>
            ) : (
              <Typography variant="body2">
                This will delete the published quiz and remove its training
                tasks. Continue?
              </Typography>
            )
          ) : (
            <Typography variant="body2">
              This removes the saved draft. Published quizzes are unaffected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteQuiz}
            disabled={deleteQuiz.isPending}
          >
            {isPublished ? "Delete published quiz" : "Delete draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
